import React from 'react';
import moment from 'moment';

// ── Benchmarked against UEDF Phocweni Clinic Audit Report ─────────────────────

const CLAUSES = [
    { code:'AACC', num:1,  title:"Access, Assessment and Continuity of Care" },
    { code:'COP',  num:2,  title:"Care of the Patient" },
    { code:'MOM',  num:3,  title:"Management of Medication" },
    { code:'PRPE', num:4,  title:"Patient's Rights and Patient's Education" },
    { code:'IPCM', num:5,  title:"Infection Prevention and Control Management" },
    { code:'PSQI', num:6,  title:"Patient Safety & Quality Improvement" },
    { code:'GROM', num:7,  title:"Governance, Roles and Responsibilities of Management" },
    { code:'FMS',  num:8,  title:"Facility Management, Safety and Support Services, Facility Risk" },
    { code:'HRE',  num:9,  title:"Human Resource and Education" },
    { code:'ISM',  num:10, title:"Information System Management" },
];

const RATING = [
    { min:98,  label:'Outstanding',          stars:5, dark:'#1b5e20', light:'#c8e6c9', bg:'#e8f5e9' },
    { min:95,  label:'Excellent',            stars:4, dark:'#0d47a1', light:'#bbdefb', bg:'#e3f2fd' },
    { min:90,  label:'Good',                 stars:3, dark:'#2e7d32', light:'#c8e6c9', bg:'#e8f5e9' },
    { min:85,  label:'Fair',                 stars:2, dark:'#e65100', light:'#fff3e0', bg:'#fff3e0' },
    { min:70,  label:'Requires Improvement', stars:1, dark:'#bf360c', light:'#fbe9e7', bg:'#fbe9e7' },
    { min:0,   label:'Inadequate',           stars:0, dark:'#b71c1c', light:'#ffcdd2', bg:'#ffcdd2' },
];
const getRating = s => RATING.find(r => parseFloat(s) >= r.min) || RATING[RATING.length-1];
const starsStr  = n => '★'.repeat(n) + '☆'.repeat(5-n);
const barColor  = s => { s=parseFloat(s); return s>=98?'#2196f3':s>=95?'#2196f3':s>=90?'#4caf50':s>=85?'#ff9800':s>=70?'#ff9800':'#f44336'; };

function ordinalDate(dateStr) {
    try {
        const d = moment(dateStr);
        const day = d.date();
        const suf = [11,12,13].includes(day%100) ? 'th' : {1:'st',2:'nd',3:'rd'}[day%10] || 'th';
        return `${day}${suf} ${d.format('MMMM YYYY')}`;
    } catch { return dateStr; }
}

// ── Shared styles matching Phocweni format ────────────────────────────────────
const DOC = {
    fontFamily: 'Times New Roman, Georgia, serif',
    fontSize: 12,
    color: '#000',
    lineHeight: 1.8,
    background: '#fff',
    width: 794,
    boxSizing: 'border-box',
};
const PAGE_PAD = '72px 90px 80px'; // top right bottom (matches A4 margins ~25mm)
const BODY = {
    fontSize: 12,
    color: '#000',
    lineHeight: 1.8,
    textAlign: 'justify',
    marginBottom: 14,
    fontFamily: 'Times New Roman, Georgia, serif',
};
const H1 = {
    fontSize: 13,
    fontWeight: 700,
    color: '#000',
    marginBottom: 14,
    marginTop: 24,
    fontFamily: 'Times New Roman, Georgia, serif',
};
const H2 = {
    fontSize: 12,
    fontWeight: 700,
    color: '#000',
    marginBottom: 10,
    marginTop: 18,
    fontFamily: 'Times New Roman, Georgia, serif',
};
const PAGE_NUM = {
    textAlign: 'center',
    fontSize: 11,
    color: '#000',
    paddingTop: 20,
    paddingBottom: 8,
    fontFamily: 'Times New Roman, Georgia, serif',
};
const TABLE_BASE = { width:'100%', borderCollapse:'collapse', marginBottom:16, fontSize:11 };
const TH_DARK = { background:'#404040', color:'#fff', padding:'7px 10px', fontWeight:700, textAlign:'center', border:'1px solid #999', fontFamily:'Arial,sans-serif' };
const TD_BASE = { padding:'6px 10px', border:'1px solid #ccc', verticalAlign:'top', fontFamily:'Arial,sans-serif', fontSize:11 };

// ── Narrative text generator matching Phocweni wording exactly ────────────────
function narrativeText(cl, sdata) {
    const pct    = parseFloat(sdata.total_score || 0);
    const metPct = parseFloat(sdata.met_pct || 0);
    const parPct = parseFloat(sdata.partially_met_pct || 0);
    const nmPct  = parseFloat(sdata.not_met_pct || 0);
    const r      = getRating(pct);

    if (pct >= 98) {
        return `The overall score achieved for this standard clause is ${pct.toFixed(1)}%. ${metPct.toFixed(1)}% of the requirements within the standard clause are fully met and ${parPct.toFixed(1)} % are partially met and not met, which indicates the need for remedial actions for those areas.`;
    }
    if (pct >= 95) {
        return `The overall score achieved for this standard clause is ${pct.toFixed(1)}%. A substantial ${metPct.toFixed(1)}% of the requirements within the clause are fully met and only ${parPct.toFixed(1)}% of the requirements are partially met with ${nmPct.toFixed(1)}% being unmet. To improve compliance with the standard clause "${cl.title}," the Hospital should focus on addressing the specific areas where requirements are partially met.`;
    }
    if (pct >= 90) {
        const combined = (parPct + nmPct).toFixed(1);
        return `The overall score achieved for this standard clause is ${pct.toFixed(1)}%. ${metPct.toFixed(1)}% of the requirements within the standard clause are fully met and ${parPct.toFixed(1)} % partially met and not met.\n\nThe total score of ${pct.toFixed(1)}% indicates significant areas for improvement, particularly concerning the fulfillment of requirements related to ${cl.title.toLowerCase()}. The score of ${metPct.toFixed(1)}% (met requirements) suggests that while the hospital has made some strides in ${cl.title.toLowerCase()}, there remains a substantial gap in compliance with established standards.\n\nThe combined total of ${combined}% (${parPct.toFixed(1)}% partially met and ${nmPct.toFixed(1)}% unmet) reflects significant areas where standards are not being fully realized. This suggests critical gaps in processes or protocols that could compromise patient safety.`;
    }
    if (pct >= 85) {
        return `The overall score achieved for this standard clause is ${pct.toFixed(1)}%. ${metPct.toFixed(1)}% of the requirements within the standard clause are fully met. Only ${parPct.toFixed(1)}% of the requirements were partially met.`;
    }
    if (pct >= 70) {
        return `The overall score achieved for this standard clause is ${pct.toFixed(1)}%. This indicates that the Hospital fully meets ${metPct.toFixed(1)}% of the requirements, ${parPct.toFixed(1)}% of the requirements are partially met and a significant ${nmPct.toFixed(1)}% of the measurable elements are not met. To improve compliance with the standard clause "${cl.title}," the facility should focus on addressing the specific areas where requirements are partially met. This could involve reviewing, developing and updating policies and procedures, and ensuring thorough documentation and monitoring practices.`;
    }
    return `The overall score achieved for this standard clause is ${pct.toFixed(1)}%. This indicates that the facility fully meets ${metPct.toFixed(1)}% of the requirements, ${parPct.toFixed(1)}% of the requirements are partially met and a significant ${nmPct.toFixed(1)}% of the measurable elements are not met.`;
}

// ── Clause summary table (3-col colored, matching Phocweni exactly) ───────────
function ClauseSummaryTable({ sdata, tableNum, clauseNum }) {
    const pct    = parseFloat(sdata.total_score || 0);
    const metPct = parseFloat(sdata.met_pct || 0);
    const parPct = parseFloat(sdata.partially_met_pct || 0);
    const nmPct  = parseFloat(sdata.not_met_pct || 0);
    const r      = getRating(pct);

    return (
        <div style={{ marginBottom: 14 }}>
            <p style={{ ...BODY, fontStyle:'italic', fontWeight:700, marginBottom:6 }}>
                Table {tableNum}, Summary of findings related to clause {clauseNum}
            </p>
            <table style={{ ...TABLE_BASE, width:'70%' }}>
                <tbody>
                    <tr>
                        <td style={{ ...TD_BASE, background:'#bdbdbd', fontWeight:700, fontFamily:'Arial,sans-serif', width:'35%' }}>
                            <strong>Total Score</strong>
                        </td>
                        <td style={{ ...TD_BASE, fontWeight:700, textAlign:'center', width:'25%' }}>
                            {pct.toFixed(1)}%
                        </td>
                        <td rowSpan={4} style={{ ...TD_BASE, background: r.light, fontWeight:700,
                            textAlign:'center', verticalAlign:'middle', color: r.dark,
                            fontSize:12, width:'40%' }}>
                            <strong>{r.label.toUpperCase()}</strong>
                        </td>
                    </tr>
                    <tr>
                        <td style={{ ...TD_BASE, background:'#4caf50', color:'#fff', fontWeight:700 }}>
                            <strong>% Met</strong>
                        </td>
                        <td style={{ ...TD_BASE, textAlign:'center' }}>{metPct.toFixed(1)}%</td>
                    </tr>
                    <tr>
                        <td style={{ ...TD_BASE, background:'#ff9800', color:'#fff', fontWeight:700 }}>
                            <strong>% Partially Met</strong>
                        </td>
                        <td style={{ ...TD_BASE, textAlign:'center' }}>{parPct.toFixed(1)}%</td>
                    </tr>
                    <tr>
                        <td style={{ ...TD_BASE, background:'#f44336', color:'#fff', fontWeight:700 }}>
                            <strong>% Not Met</strong>
                        </td>
                        <td style={{ ...TD_BASE, textAlign:'center' }}>{nmPct.toFixed(1)}%</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

// ── Nonconformity table (Area | Observation | Good Practice) ──────────────────
function NonconfTable({ mes, tableNum, clauseNum }) {
    // Build rows from ME responses with real comments/observations/good practices
    const rows = [];
    const byArea = {};

    mes.forEach(r => {
        if (!['Not Met','Partially Met'].includes(r.fulfillment)) return;
        const area = (r.sub_requirement || '').trim() || 'General';
        if (!byArea[area]) byArea[area] = [];
        byArea[area].push(r);
    });

    Object.entries(byArea).forEach(([area, items]) => {
        items.forEach((r, i) => {
            const obs = (r.comment || r.general_observation || '').trim();
            const gp  = (r.area_of_good_practice || '').trim();
            if (!obs && !gp) return;
            rows.push({
                area:   i === 0 ? area : '',
                obs:    obs,
                gp:     gp,
            });
        });
    });

    if (!rows.length) return null;

    return (
        <div style={{ marginBottom:16 }}>
            <p style={{ ...BODY, fontStyle:'italic', fontWeight:700, marginBottom:6 }}>
                Table {tableNum}, Findings that were attributed to the "Not met" and "Partially Met" (nonconformities), under clause {clauseNum}.
            </p>
            <table style={{ ...TABLE_BASE }}>
                <thead>
                    <tr>
                        <th style={{ ...TH_DARK, width:'25%', textAlign:'left' }}>Area/ Process</th>
                        <th style={{ ...TH_DARK, width:'45%', textAlign:'left' }}>Non-conformity /observation</th>
                        <th style={{ ...TH_DARK, width:'30%', textAlign:'left' }}>Good practices</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} style={{ background: i%2 ? '#fafafa' : '#fff' }}>
                            <td style={{ ...TD_BASE, fontWeight: row.area ? 600 : 400,
                                color: row.area ? '#000' : 'transparent', userSelect: row.area ? 'auto' : 'none' }}>
                                {row.area || '-'}
                            </td>
                            <td style={{ ...TD_BASE }}>{row.obs || ''}</td>
                            <td style={{ ...TD_BASE }}>{row.gp || ''}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── Bar chart — CSS bars matching the Phocweni matplotlib chart ───────────────
function OverallBarChart({ clauseScores, overall }) {
    const bars = [
        ...CLAUSES.filter(c => clauseScores[c.code]).map(c => ({
            label: c.code,
            pct: parseFloat(clauseScores[c.code]?.total_score || 0),
        })),
        { label: 'Overall\nScore', pct: parseFloat(overall) },
    ];

    const maxH = 200; // px chart area height

    return (
        <div style={{ border:'1px solid #e0e0e0', borderRadius:4, padding:'20px 16px 8px', background:'#fafafa', marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, textAlign:'center', marginBottom:16, fontFamily:'Arial,sans-serif', color:'#000' }}>
                Overall Quality-Of-Care Audit Findings
            </div>
            {/* Y-axis labels + bars */}
            <div style={{ display:'flex', gap:0 }}>
                {/* Y axis */}
                <div style={{ display:'flex', flexDirection:'column-reverse', justifyContent:'space-between',
                    paddingRight:6, paddingBottom:32, fontSize:9, color:'#555', fontFamily:'Arial,sans-serif', width:52, textAlign:'right' }}>
                    {[0,20,40,60,80,100,120].map(v => (
                        <span key={v}>{v.toFixed(2)}%</span>
                    ))}
                </div>
                {/* Chart area */}
                <div style={{ flex:1, position:'relative' }}>
                    {/* Horizontal grid lines */}
                    {[0,20,40,60,80,100].map(v => (
                        <div key={v} style={{ position:'absolute', left:0, right:0,
                            bottom: 32 + (v/120)*maxH,
                            borderTop:'1px dashed #ddd', zIndex:0 }}/>
                    ))}
                    {/* 90% green dashed */}
                    <div style={{ position:'absolute', left:0, right:0,
                        bottom: 32 + (90/120)*maxH,
                        borderTop:'1.5px dashed #4caf50', zIndex:1, opacity:0.6 }}/>
                    {/* 70% red dashed */}
                    <div style={{ position:'absolute', left:0, right:0,
                        bottom: 32 + (70/120)*maxH,
                        borderTop:'1.5px dashed #f44336', zIndex:1, opacity:0.5 }}/>
                    {/* Bars */}
                    <div style={{ display:'flex', alignItems:'flex-end', height: maxH + 32, gap:4, paddingBottom:32, position:'relative', zIndex:2 }}>
                        {bars.map((b, i) => {
                            const barH = Math.max(2, (b.pct / 120) * maxH);
                            const bc = barColor(b.pct);
                            const isLast = i === bars.length - 1;
                            return (
                                <div key={b.label} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center' }}>
                                    <div style={{ fontSize:8, fontWeight:700, color:'#333', marginBottom:2,
                                        fontFamily:'Arial,sans-serif', whiteSpace:'nowrap' }}>
                                        {b.pct.toFixed(2)}%
                                    </div>
                                    <div style={{ width:'75%', background:bc, height: barH,
                                        borderRadius:'2px 2px 0 0',
                                        border: isLast ? '2px solid rgba(0,0,0,0.2)' : 'none' }}/>
                                </div>
                            );
                        })}
                    </div>
                    {/* X-axis labels */}
                    <div style={{ display:'flex', gap:4, marginTop:-28 }}>
                        {bars.map(b => (
                            <div key={b.label} style={{ flex:1, textAlign:'center', fontSize:8.5,
                                color:'#444', fontFamily:'Arial,sans-serif', lineHeight:1.2 }}>
                                {b.label.split('\n').map((line, i) => <div key={i}>{line}</div>)}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Main document ─────────────────────────────────────────────────────────────
export default function NarrativeReportDocument({ facility, assessment, clauseScores, responses, capItems = [], auditTeam = null }) {
    if (!assessment || !facility) return null;

    const overall   = parseFloat(assessment.overall_percentage || 0);
    const rating    = getRating(overall);
    const auditDate = assessment.assessment_date || '';
    const fname     = facility.business_name || 'Health Facility';
    const assessor  = assessment.assessor_name || 'National Quality Management Programme';

    const clauseMap = {};
    clauseScores.forEach(s => { if (s.clause_code) clauseMap[s.clause_code] = s; });

    const meByClause = {};
    responses.forEach(r => {
        if (!meByClause[r.clause_code]) meByClause[r.clause_code] = [];
        meByClause[r.clause_code].push(r);
    });

    const scoreGuide = [
        { range:'98 - 100%', label:'Outstanding',          rec:'Ready for ISO accreditation',          stars:5, rowBg:'#fff9c4', labelColor:'#f57f17' },
        { range:'95 - 97.9%',label:'Excellent',            rec:'Ready for ISO accreditation',          stars:4, rowBg:'#c8e6c9', labelColor:'#2e7d32' },
        { range:'90 - 94.9%',label:'Good',                 rec:'Almost ready for ISO accreditation',   stars:3, rowBg:'#c8e6c9', labelColor:'#2e7d32' },
        { range:'85 - 89.9%',label:'Fair',                 rec:'Somewhat ready for ISO accreditation', stars:2, rowBg:'#fff3e0', labelColor:'#e65100' },
        { range:'70 - 84.9%',label:'Requires improvement', rec:'Not ready for ISO accreditation',      stars:1, rowBg:'#fff3e0', labelColor:'#e65100' },
        { range:'< 70%',     label:'Inadequate',           rec:'Cause for concern',                    stars:0, rowBg:'#ffcdd2', labelColor:'#b71c1c' },
    ];

    // Build audit team text
    const leadName  = auditTeam?.lead_auditor_name || '';
    const members   = auditTeam?.members || [];
    const allNames  = [leadName, ...members.filter(m=>m.role!=='Lead Auditor').map(m=>m.name||m.user_name)].filter(Boolean);
    const depts     = auditTeam?.departments_visited || [];

    // Clause table numbers: clause 1 = table 2, clause 2 = table 4 etc (summary + nonconf pairs)
    const getTableNums = (clauseIdx) => ({
        summary:   (clauseIdx * 2) + 2,
        nonconf:   (clauseIdx * 2) + 3,
    });

    return (
        <div id="narrative-report" style={{ ...DOC }}>

            {/* ══ COVER PAGE ══════════════════════════════════════════════════ */}
            <div className="narrative-page" style={{ padding:'120px 90px 80px', textAlign:'center', pageBreakBefore:'always', pageBreakInside:'avoid', display:'flex',
                flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                <p style={{ fontSize:16, fontWeight:700, marginBottom:16, fontFamily:'Times New Roman, Georgia, serif' }}>
                    FACILITY: {fname.toUpperCase()}
                </p>
                <p style={{ fontSize:14, fontWeight:700, marginBottom:48, fontFamily:'Times New Roman, Georgia, serif' }}>
                    QUALITY OF CARE AUDIT REPORT
                </p>
                <p style={{ fontSize:12, marginBottom:4, fontFamily:'Times New Roman, Georgia, serif' }}>Prepared by:</p>
                <p style={{ fontSize:12, fontWeight:700, fontFamily:'Times New Roman, Georgia, serif' }}>National Quality Management Program</p>
                <p style={{ fontSize:12, fontFamily:'Times New Roman, Georgia, serif' }}>Ministry of Health</p>
            </div>

            {/* ══ EXECUTIVE SUMMARY ══════════════════════════════════════════ */}
            <div className="narrative-page" style={{ padding: PAGE_PAD, pageBreakBefore:"always", pageBreakInside:"avoid" }}>
                <p style={H1}><strong>EXECUTIVE SUMMARY</strong></p>
                <p style={BODY}>
                    The Ministry of Health through the Quality Management Program developed the Quality of
                    Care standards which have been reviewed and have undertaken the gazette process by the
                    Eswatini Standard Authority (SWASA). These standards are at the stage of being rolled out to
                    facilities and will be introduced in the first phase across 24 facilities. The purpose of this
                    initiative is to ensure that facilities maintain the highest standards of care delivery, adhering to
                    nationally recognized benchmarks for quality improvement. The standard encompasses
                    comprehensive guidelines aimed at enhancing patient outcomes, improving service delivery,
                    and promoting overall operational efficiency.
                </p>
                <p style={BODY}>
                    With the developed standards, an audit was conducted at {fname} on the {ordinalDate(auditDate)}.
                    The facility has shown great strengths in the provision of quality services with an
                    overall score of {overall.toFixed(1)}% (rated as {rating.label.toUpperCase()} ({starsStr(rating.stars)})
                    {' '}according to the QOC rating and at {rating.stars} Star{rating.stars !== 1 ? 's' : ''}).
                </p>
                <p style={BODY}>
                    This report provides comprehensive findings on the implementation of the Quality of Care
                    Standards within the health facility. It includes relevant recommendations aimed at helping the
                    facility maintain and enhance its performance based on the audit results.
                </p>
                <div style={{ marginTop:60 }}>
                    <div style={{ fontSize:12, fontFamily:'Times New Roman, Georgia, serif',
                        borderBottom:'1px solid #000', width:180, marginBottom:4 }}>&nbsp;</div>
                    <p style={{ fontSize:12, fontFamily:'Times New Roman, Georgia, serif', margin:0 }}>{assessor}</p>
                    <p style={{ fontSize:12, fontFamily:'Times New Roman, Georgia, serif', margin:0 }}>Quality of Care Technical Lead</p>
                </div>
                <p style={PAGE_NUM}>1</p>
            </div>

            {/* ══ CONTENTS ═══════════════════════════════════════════════════ */}
            <div className="narrative-page" style={{ padding: PAGE_PAD, pageBreakBefore:"always", pageBreakInside:"avoid" }}>
                <p style={H1}><strong>Contents</strong></p>
                {[
                    ['EXECUTIVE SUMMARY', '1'],
                    ['ABBREVIATIONS', '2'],
                    ['1. INTRODUCTION', '4'],
                    ['   1.2 SCOPE OF THE AUDIT', '4'],
                    ['   1.3 OBJECTIVES OF THE AUDIT', '4'],
                    ['2. METHODOLOGY', '5'],
                    ['3. FINDINGS', '6'],
                    ['   3.1 Overall Audit Results', '6'],
                    ['   3.2 Audit Results per Standard', '7'],
                    ...CLAUSES.map((cl,i) => [`   Clause ${cl.num}: ${cl.title} (${cl.code})`, String(7+i)]),
                    ['6. CONCLUSION', String(7 + CLAUSES.length)],
                ].map(([label, pg], i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between',
                        padding:'3px 0', borderBottom:'1px dotted #ccc',
                        fontWeight: !label.startsWith(' ') ? 700 : 400,
                        fontSize: 12, fontFamily:'Times New Roman, Georgia, serif' }}>
                        <span>{label.trim()}</span>
                        <span>{pg}</span>
                    </div>
                ))}
                <p style={{ ...H1, marginTop:28 }}><strong>ABBREVIATIONS</strong></p>
                {[
                    'EHCP- Essential Health Care Package',
                    'ISO- International Organization for Standards',
                    'QOC- Quality of Care',
                    'NQMP-National Quality Management Program',
                    'SOP- Standard Operating Procedure',
                    'MoH- Ministry of Health',
                    'SWASA- Eswatini Standard Authority',
                    'IPC- Infection Prevention and Control',
                ].map((a,i) => (
                    <p key={i} style={{ ...BODY, marginBottom:4 }}>{a}</p>
                ))}
                <p style={PAGE_NUM}>2</p>
            </div>

            {/* ══ INTRODUCTION ═══════════════════════════════════════════════ */}
            <div className="narrative-page" style={{ padding: PAGE_PAD, pageBreakBefore:"always", pageBreakInside:"avoid" }}>
                <p style={H1}><strong>1. INTRODUCTION</strong></p>
                <p style={BODY}>
                    The Eswatini Ministry of Health strives to provide comprehensive high quality health services
                    to the population. In line with this goal the Ministry through the National Quality Management
                    Program (NQMP) developed guidelines for the Eswatini QoC Standards to deliver standardized
                    care by facilities as categorized by the Essential Healthcare Package (EHCP ll 2017). The
                    objective of the QoC Standards, is to be a guide of information, as well as placing the focus on
                    the dimensions of quality, that is, effective, efficient, people centred, equitable and integrated
                    access to safe quality care. The standard clauses are arranged in a manner that would represent
                    the patient's journey throughout the facility, from admission through to discharge. These
                    standards will rate Eswatini's healthcare amongst the countries that use similar international
                    standards.
                </p>
                <p style={BODY}>
                    The QoC standards come with an implementation framework which, among other things,
                    requires that a baseline assessment is done in the implementing facility, remedial actions
                    implemented through continuous quality improvement to enable a facility achieve the highest
                    score in subsequent QoC audits and get ISO accreditation.
                </p>
                <p style={BODY}>
                    This report presents the audit findings for {fname} conducted on the {ordinalDate(auditDate)},
                    under the Eswatini Quality of Care standards.
                </p>

                <p style={H2}><strong>1.2 SCOPE OF THE AUDIT</strong></p>
                <p style={BODY}>
                    The audit focused on evaluating the quality of care provided by {fname}, in all healthcare
                    service areas. This encompasses a comprehensive assessment of the facility's adherence to
                    established standards and protocols in these critical areas of healthcare delivery.
                </p>

                <p style={H2}><strong>1.3 OBJECTIVES OF THE AUDIT</strong></p>
                {[
                    'To obtain a baseline on the quality-of-care standard.',
                    "To identify the strengths and weaknesses of the facility's current healthcare delivery system in the specified areas and provide actionable insights for performance improvement.",
                    'To uncover opportunities for quality improvement within the health facility, with a focus on enhancing the overall quality of care provided to patients.',
                    `To benchmark from ${fname} best practices in health care delivery.`,
                    'To provide an opportunity for stakeholder engagement and collaboration, fostering a culture of transparency, accountability, and continuous quality improvement within the facility.',
                ].map((obj, i) => (
                    <div key={i} style={{ display:'flex', gap:10, marginBottom:10, fontSize:12,
                        fontFamily:'Times New Roman, Georgia, serif', textAlign:'justify' }}>
                        <span style={{ minWidth:18 }}>{i+1}.</span>
                        <span>{obj}</span>
                    </div>
                ))}
                <p style={PAGE_NUM}>4</p>
            </div>

            {/* ══ METHODOLOGY ════════════════════════════════════════════════ */}
            <div className="narrative-page" style={{ padding: PAGE_PAD, pageBreakBefore:"always", pageBreakInside:"avoid" }}>
                <p style={H1}><strong>2. METHODOLOGY</strong></p>
                <p style={BODY}>
                    The MoH quality-of-care audit tool was digitised, uploaded on tablets and used to conduct the
                    audit on all the ten (10) clauses of the standard. Each clause has requirements and measurable
                    elements where the facility may be rated as "Met" the requirement, "Partially Met", and "Not
                    met". Results from each requirement are computed and a percentage score given. The table
                    below provides the interpretation of percentage scores, a recommendation based on the score
                    and star rating.
                </p>
                <p style={{ ...BODY, fontStyle:'italic', fontWeight:700, marginBottom:8 }}>Table 1: Audit scoring guide</p>
                <table style={{ ...TABLE_BASE, width:'100%' }}>
                    <thead>
                        <tr>
                            {['Score Range','Interpretation','Recommendation','STAR Rating'].map(h => (
                                <th key={h} style={{ ...TH_DARK, textAlign:'center' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {scoreGuide.map(row => (
                            <tr key={row.label} style={{ background: row.rowBg }}>
                                <td style={{ ...TD_BASE, textAlign:'center', fontWeight:700, color: row.labelColor }}>{row.range}</td>
                                <td style={{ ...TD_BASE, textAlign:'center', fontWeight:700, color: row.labelColor }}>{row.label}</td>
                                <td style={{ ...TD_BASE, textAlign:'center' }}>{row.rec}</td>
                                <td style={{ ...TD_BASE, textAlign:'center', fontSize:14, color:'#f9a825' }}>{starsStr(row.stars)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {allNames.length > 0 && (
                    <>
                        <p style={BODY}>The audit team comprised of the following:</p>
                        <p style={BODY}>
                            Lead Auditor-{allNames.join(', ')}; collectively possessing diverse expertise in healthcare
                            quality management, patient care, and regulatory compliance. This multidisciplinary team
                            was carefully selected to ensure comprehensive coverage of the audit objectives.
                            Their roles and responsibilities include;
                        </p>
                        <p style={BODY}>
                            <strong>Audit Team Lead</strong>- Planning the audit, determining audit schedule, determining team size,
                            assigning workload to the audit team, leading the proceedings of the audit and preparing the Audit Report.
                        </p>
                    </>
                )}
                {!allNames.length && (
                    <p style={BODY}>The audit team comprised of the following:</p>
                )}
                <p style={BODY}>
                    <strong>Auditors</strong>-Audit planning with lead auditor, audit execution within scope of audit, documenting
                    audit findings and reporting to lead auditor, follow up and verification of corrective actions on non-conformances.
                </p>
                <p style={BODY}>
                    The audit process involved a combination of on-site inspections, document reviews, and breakaway sessions.
                    During breakaway sessions, sub-teams visited the relevant departments and consultation rooms to engage with
                    relevant facility personnel and stakeholders, observe, gather insights and exchange information relevant to
                    the audit questions. Following the breakaway sessions, sub-teams reconvened to consolidate their findings and
                    ensure completeness of the audit, consistency across audited clauses, this included cross-referencing data,
                    triangulating information, and conducting peer reviews within the audit team.
                </p>
                <p style={PAGE_NUM}>5</p>
            </div>

            {/* ══ FINDINGS — OVERALL ═════════════════════════════════════════ */}
            <div className="narrative-page" style={{ padding: PAGE_PAD, pageBreakBefore:"always", pageBreakInside:"avoid" }}>
                <p style={H1}><strong>3. FINDINGS</strong></p>
                <p style={BODY}>
                    This section presents the key findings resulting from the comprehensive audit conducted at
                    {' '}{fname}. Through evaluation and analysis, the audit team assessed various aspects of
                    healthcare delivery, in relation to the quality-of-care standards. These findings serve to
                    provide an objective assessment of the clinic's performance, highlighting areas of strength and
                    areas warranting improvement.
                </p>
                <p style={BODY}>
                    Each subsection within this section is dedicated to specific audit standards, facilitating a
                    structured presentation of findings for enhanced clarity and actionable insights.
                </p>

                <p style={H2}><strong>3.1 Overall Audit Results</strong></p>
                <p style={H2}><strong>3.1 FINDINGS ON THE QUALITY OF CARE STANDARD</strong></p>
                <p style={BODY}>Figure 1, illustration of the overall quality of care audit findings.</p>

                <OverallBarChart clauseScores={clauseMap} overall={overall} />

                <p style={BODY}>
                    Figure 1 above illustrates the overall audit results, with the colors of the bars indicating
                    the corresponding star ratings as detailed in Table 1. The table below shows the star rating
                    of the health facility according to the quality of care tool.
                </p>

                {/* Overall summary table */}
                <table style={{ ...TABLE_BASE, width:'60%' }}>
                    <thead>
                        <tr>
                            <th style={{ ...TH_DARK }}>Overall</th>
                            <th style={{ ...TH_DARK }}>Interpretation</th>
                            <th style={{ ...TH_DARK }}>Star Rating</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{ background: rating.light }}>
                            <td style={{ ...TD_BASE, textAlign:'center', fontWeight:700, color: rating.dark, fontSize:14 }}>{overall.toFixed(2)}%</td>
                            <td style={{ ...TD_BASE, textAlign:'center', fontWeight:700, color: rating.dark }}>{rating.label}</td>
                            <td style={{ ...TD_BASE, textAlign:'center', fontSize:16, color:'#f9a825' }}>{starsStr(rating.stars)}</td>
                        </tr>
                    </tbody>
                </table>

                <p style={{ ...H2, marginTop:20 }}><strong>3.2 Audit Results per Standard</strong></p>

                {/* First clause starts on this page */}
                {CLAUSES.filter(cl => clauseMap[cl.code]).slice(0,1).map(cl => {
                    const sdata = clauseMap[cl.code];
                    const mes   = meByClause[cl.code] || [];
                    const nums  = getTableNums(0);
                    return (
                        <div key={cl.code}>
                            <p style={H2}><strong>Clause {cl.num}: {cl.title} ({cl.code})</strong></p>
                            <ClauseSummaryTable sdata={sdata} tableNum={nums.summary} clauseNum={cl.num}/>
                            {narrativeText(cl, sdata).split('\n\n').map((para, i) => (
                                <p key={i} style={BODY}>{para}</p>
                            ))}
                            <NonconfTable mes={mes} tableNum={nums.nonconf} clauseNum={cl.num}/>
                        </div>
                    );
                })}
                <p style={PAGE_NUM}>7</p>
            </div>

            {/* ══ REMAINING CLAUSES (one section per page) ═══════════════════ */}
            {CLAUSES.filter(cl => clauseMap[cl.code]).slice(1).map((cl, idx) => {
                const sdata = clauseMap[cl.code];
                const mes   = meByClause[cl.code] || [];
                const nums  = getTableNums(idx + 1);
                const pageNum = 8 + idx;
                return (
                    <div key={cl.code} className="narrative-page" style={{ padding: PAGE_PAD, pageBreakBefore:"always", pageBreakInside:"avoid" }}>
                        <p style={H2}><strong>Clause {cl.num}: {cl.title} ({cl.code})</strong></p>
                        <ClauseSummaryTable sdata={sdata} tableNum={nums.summary} clauseNum={cl.num}/>
                        {narrativeText(cl, sdata).split('\n\n').map((para, i) => (
                            <p key={i} style={BODY}>{para}</p>
                        ))}
                        <NonconfTable mes={mes} tableNum={nums.nonconf} clauseNum={cl.num}/>
                        <p style={PAGE_NUM}>{pageNum}</p>
                    </div>
                );
            })}

            {/* ══ CAP (if items exist) ════════════════════════════════════════ */}
            {capItems.length > 0 && (
                <div className="narrative-page" style={{ padding: PAGE_PAD, pageBreakBefore:"always", pageBreakInside:"avoid" }}>
                    <p style={H1}><strong>4. CORRECTIVE ACTION PLAN</strong></p>
                    <p style={BODY}>
                        Based on the audit findings, <strong>{capItems.length}</strong> corrective action items have been
                        identified — <strong>{capItems.filter(c=>['open','in_progress','overdue'].includes(c.status)).length}</strong> open
                        and requiring remediation, <strong>{capItems.filter(c=>c.status==='closed').length}</strong> already closed.
                    </p>
                    <table style={TABLE_BASE}>
                        <thead>
                            <tr>
                                {['Ref No','Finding','Root Cause','Action Required','Responsible','Target Date','Status'].map(h=>(
                                    <th key={h} style={{ ...TH_DARK, fontSize:10 }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {capItems.map((c,i) => {
                                const sc = c.status==='closed'?'#2e7d32':c.status==='overdue'?'#b71c1c':'#1565c0';
                                return (
                                    <tr key={i} style={{ background:i%2?'#f9f9f9':'#fff' }}>
                                        <td style={{ ...TD_BASE, fontSize:10 }}>{c.ref_no||'—'}</td>
                                        <td style={{ ...TD_BASE, fontSize:10 }}>{(c.finding||'').slice(0,80)}</td>
                                        <td style={{ ...TD_BASE, fontSize:10 }}>{c.root_cause||'—'}</td>
                                        <td style={{ ...TD_BASE, fontSize:10 }}>{(c.action_description||'').slice(0,80)||'—'}</td>
                                        <td style={{ ...TD_BASE, fontSize:10 }}>{c.responsible_person||'—'}</td>
                                        <td style={{ ...TD_BASE, fontSize:10, textAlign:'center' }}>{c.target_date?moment(c.target_date).format('DD MMM YYYY'):'—'}</td>
                                        <td style={{ ...TD_BASE, fontSize:10, textAlign:'center', fontWeight:700, color:sc }}>{(c.status||'').toUpperCase()}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ══ CONCLUSION ═════════════════════════════════════════════════ */}
            <div className="narrative-page" style={{ padding: PAGE_PAD, pageBreakBefore:"always", pageBreakInside:"avoid" }}>
                <p style={H1}><strong>6. CONCLUSION</strong></p>
                <p style={BODY}>
                    The audit conducted at {fname} underscores the commitment to delivering high-quality
                    healthcare services while identifying areas for improvement. By implementing the outlined
                    Non-conformances, {fname} can further enhance patient safety, effectiveness, and overall
                    patient experience, thereby ensuring continued excellence in healthcare service delivery.
                </p>
                {(() => {
                    const weak = CLAUSES
                        .filter(cl => clauseMap[cl.code] && parseFloat(clauseMap[cl.code]?.total_score||0) < 90)
                        .sort((a,b) => parseFloat(clauseMap[a.code]?.total_score||0) - parseFloat(clauseMap[b.code]?.total_score||0))
                        .slice(0,3)
                        .map(cl => cl.code);
                    if (!weak.length) return null;
                    return (
                        <p style={BODY}>
                            Particular attention should be directed towards strengthening compliance
                            in <strong>{weak.join(', ')}</strong>, where targeted quality improvement activities,
                            policy reviews, and staff capacity building will be critical for achieving higher scores
                            in subsequent audits and progressing towards ISO accreditation.
                        </p>
                    );
                })()}
                <p style={PAGE_NUM}>{7 + CLAUSES.filter(cl => clauseMap[cl.code]).length}</p>
            </div>

        </div>
    );
}